import {
	ReactElement,
	useCallback,
	useMemo
} from "react";
import { CommunityCoverImageViewModel } from "client/Api/Communities/community";
import { HubDataSource } from "client/Api/Hub/hub";
import { Anchor, Icon } from "client/Components";
import { getCommunityImageUrl } from "client/Features/Hub/Communities/common";
import { getHubUrlForCommunity } from "client/Features/Hub/Communities/CommunitiesPage";
import {
	generateTrackingId,
	isNil,
	setTrackingId
} from "client/Misc/utils";
import { localize } from "client_legacy/ndaw/culture";
import { navigateTo } from "client_legacy/ndaw/navigator";
import { ImageWithFocalPoint } from "client/Features/Hub/FocalPointDialog/ImageWithFocalPoint";
import styles from "./NewCommunities.module.less";
import classNames from "classnames";
import { NewCommunityItemViewModel } from "client/Api/Communities/communityFeed";

export type NewCommunitiesProps = {
	communities: readonly NewCommunityItemViewModel[];
	onAddAnalyticsEvent: () => void;
	onNavigateToCommunity: (communityId: number) => void;
};

export const NewCommunities = (props: NewCommunitiesProps): ReactElement => {
	const {
		communities,
		onAddAnalyticsEvent,
		onNavigateToCommunity
	} = props;

	const navigateToDiscoverPage = useCallback(
		(): void => {
			onAddAnalyticsEvent();

			const url: string = getHubUrlForCommunity({
				source: HubDataSource.Community
			});

			navigateTo(url);
		},
		[onAddAnalyticsEvent]
	);

	const communitesToRender = useMemo(
		(): readonly NewCommunityItemViewModel[] => communities.toReversed(),
		[communities]
	);

	return (
		<div
			{...setTrackingId("new-communities")}
			className={styles.root}
		>
			<div className={styles.title}>
				{localize("_NewCommunities")}
			</div>
			<div className={styles.listAndMoreButton}>
				<ul className={styles.list}>
					{communitesToRender.map(
						(x): ReactElement => (
							<li
								key={x.id}
								className={styles.listItem}
							>
								<Item
									trackingId={generateTrackingId("item", `|${x.id}`)}
									image={x.image}
									name={x.name}
									onClick={() => onNavigateToCommunity(x.id)}
								/>
							</li>
						)
					)}
				</ul>
				<div className={styles.moreButton}>
					<AllCommunitiesButton
						onClick={navigateToDiscoverPage}
					/>
				</div>
			</div>
		</div>
	);
};
NewCommunities.displayName = "NewCommunities";

type ItemProps = {
	image: CommunityCoverImageViewModel | null;
	name: string;
	trackingId: string;
	onClick: () => void;
};

const Item = (props: ItemProps): ReactElement => {
	const {
		image,
		name,
		trackingId,
		onClick
	} = props;

	const renderThumbnail = (): ReactElement => {
		if (isNil(image)) {
			return (
				<Icon
					value="users"
					size="large"
				/>
			);
		}

		const {
			file,
			focalPoint
		} = image;

		const {
			blobId,
			filename
		} = file;

		return (
			<ImageWithFocalPoint
				src={getCommunityImageUrl(blobId)}
				focalPoint={focalPoint}
				alt={filename}
				height="fill"
			/>
		);
	};

	return (
		<Anchor
			trackingId={trackingId}
			className={styles.itemContent}
			onClick={onClick}
		>
			<div
				className={classNames(
					styles.itemThumbnail,
					{[styles.itemThumbnail_hasNoImage]: isNil(image)}
				)}
			>
				{renderThumbnail()}
			</div>
			<div className={styles.itemName}>
				{name}
			</div>
		</Anchor>
	);
};
Item.displayName = "Item";


type AllCommunitiesButtonProps = {
	onClick: () => void;
};

const AllCommunitiesButton = (props: AllCommunitiesButtonProps): ReactElement => {
	const {
		onClick
	} = props;

	return (
		<Anchor
			trackingId="all-communities"
			className={styles.itemContent}
			onClick={onClick}
		>
			<div
				className={classNames(
					styles.itemThumbnail,
					styles.itemThumbnail_hasBorder
				)}
			>
				<Icon
					value="arrow-right"
					size="large"
				/>
			</div>
			<div className={styles.itemName}>
				{localize("_AllCommunities")}
			</div>
		</Anchor>
	);
};
AllCommunitiesButton.displayName = "AllCommunitiesButton";